/**
 * 跨端会话关联的 web 半边 —— 把 web 的 OpenReplay sessionId 回传给原生。
 *
 * ## 为什么需要它
 *
 * 两端各自建立独立的 OpenReplay session（web 在 WebView 里，原生用移动 SDK）。
 * 要在后台把一条用户链路拼起来，必须**互指**：
 *
 *   web 侧   metadata `native_or_session` ← 原生注入（`applyNativeContext`）
 *   原生侧   metadata `web_or_session`    ← **本文件回传**（`WebBridge.kt` 收 → `setMetadata`）
 *
 * 少了回传就只能单向定位：从 web session 能找到原生 session，反过来不行。
 * 契约见 `docs/engineering/native-web-bridge-contract.md` §3.2 `tracking-web-session`。
 *
 * ## 为什么要重试
 *
 * `tracker.getSessionID()` 在 session 真正建立前返回 `null`/`undefined`
 * （SDK：`app === null` 时 null，session 未开始时 `sessionID || undefined`）。
 * 而 tracker 启动是异步的（动态 import + 与 ingest 握手），所以**必须轮询**，
 * 不能启动后立刻读一次就算完。
 *
 * 上限是有意的：拿不到就放弃，不无限轮询 —— 埋点不许拖住业务，也不许留常驻定时器。
 */

import { getBridge } from "@/src/lib/bridge/bridge";
import { getTracker } from "@utils/openreplay";
import { container } from "./envelope";
import { Track } from "./index";

/** 桥消息类型（与 `WebBridge.kt` 的 `"tracking-web-session"` 分支一致）。 */
const MESSAGE_TYPE = "tracking-web-session";

/** 轮询上限：10 次 × 300ms ≈ 3s，覆盖正常启动握手，超了就认命。 */
const MAX_ATTEMPTS = 10;
const INTERVAL_MS = 300;

/**
 * 回传 sessionId；返回取消函数（组件卸载时停掉未完成的轮询）。
 *
 * 纯浏览器下直接 no-op —— 没有原生可回传。
 */
export function reportWebSessionToNative(): () => void {
  if (container() !== "webview") {
    return () => {};
  }

  let attempts = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let cancelled = false;

  const tick = () => {
    if (cancelled) {
      return;
    }
    attempts++;
    let sessionId: string | null | undefined;
    try {
      sessionId = getTracker()?.getSessionID?.();
    } catch {
      sessionId = undefined; // 埋点不影响业务
    }

    if (sessionId) {
      try {
        getBridge().send(MESSAGE_TYPE, { sessionId });
      } catch (e) {
        Track.log("warn", "Tracking", "回传 web sessionId 失败", {
          cause: String(e),
        });
      }
      return;
    }

    if (attempts >= MAX_ATTEMPTS) {
      // 说出来而不是静默失败：`web_or_session` 为空时后台是"能查但查不全"，
      // 没有这行日志就没人知道链路是断的。
      Track.log(
        "warn",
        "Tracking",
        "拿不到 web sessionId，跨端链路将缺 web_or_session",
        {
          attempts,
        },
      );
      return;
    }
    timer = setTimeout(tick, INTERVAL_MS);
  };

  tick();

  return () => {
    cancelled = true;
    if (timer) {
      clearTimeout(timer);
    }
  };
}
